import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

import { HTTPErrorData } from './data/types';
import TimeTracking from './modules/TimeTacking';

import {
    AddonTask,
    AiImage,
    AiTaskBrief,
    Auth,
    BrandProfile,
    BrandProfileAsset,
    ClientFeedback,
    ClientSignup,
    ClientUser,
    ComputedLicence,
    CoreUser,
    Coupon,
    CreditWallet,
    Email,
    File,
    Filter,
    IllustrationLib,
    Invoice,
    Licence,
    Me,
    Notification,
    OpenTaskList,
    PaymentAccount,
    PaymentMethod,
    Plan,
    Price,
    Product,
    QuotaUsage,
    Subscription,
    SubscriptionPlan,
    Task,
    TaskCategory,
    TaskCategoryAddon,
    TaskCategoryFeaturedBlock,
    TaskCategoryGroup,
    TaskClientFeedback,
    TaskComplexity,
    TaskDeliverable,
    TaskDeliverableComment,
    TaskDeliverablePack,
    TaskIssue,
    TaskKanban,
    TaskMessage,
    TaskMessageTemplate,
    TaskMessageTemplateCategory,
    TaskPublish,
    TaskSubTicket,
    TaxId,
    Team,
    TeamOccupancy,
    TimeTacking,
    UIUserTask,
    User,
    UserEmailChange,
    UserNote,
    UserNotificationEmail,
    UserOccupancy,
    UserPasswordReset,
    UserSpace,
    UserVerification,
    UserVerificationCode,
    VirtualLicence,
    Workspace,
    WorkspaceInitData,
    WorkspaceKey,
    WorkspaceSignup,
    WorkspaceUser,
} from './modules';

class Repository {
    private readonly http: AxiosInstance;

    constructor() {
        // Configuration properties
        let baseURL = 'http://localhost:8080';
        const timeout = 25000;
        const headers = { Accept: 'application/json', 'Content-type': 'application/json' };

        // Check if environment variable for "API_BASE_URL" was set
        if (process.env.API_BASE_URL) {
            baseURL = process.env.API_BASE_URL;
        }

        this.http = axios.create({ baseURL, timeout, headers });
    }

    removeAxiosResponseInterceptor({ id }: { id: number }): void {
        this.http.interceptors.response.eject(id);
    }

    addAxiosResponseInterceptor({
        onSuccess,
        onFail,
    }: {
        onSuccess?: (response: AxiosResponse) => void;
        onFail?: (error: AxiosError<HTTPErrorData>) => void;
    }): number {
        return this.http.interceptors.response.use(
            (response) => {
                onSuccess?.(response);
                return response;
            },
            (error) => {
                onFail?.(error);
                return Promise.reject(error);
            }
        );
    }

    setAuthAccessTokenHeader = (token: string, tokenType: string = 'Bearer') => {
        this.http.defaults.headers['Authorization'] = `${tokenType} ${token}`;
    };

    removeAuthAccessTokenHeader = () => {
        delete this.http.defaults.headers['Authorization'];
    };

    /* Modules */
    addonTask = (): AddonTask => {
        return new AddonTask(this.http);
    };

    aiImage = (): AiImage => {
        return new AiImage(this.http);
    };

    aiTaskBrief = (): AiTaskBrief => {
        return new AiTaskBrief(this.http);
    };

    auth = (): Auth => {
        return new Auth(this.http);
    };

    brandProfile = (): BrandProfile => {
        return new BrandProfile(this.http);
    };

    brandProfileAsset = (): BrandProfileAsset => {
        return new BrandProfileAsset(this.http);
    };

    clientFeedback = (): ClientFeedback => {
        return new ClientFeedback(this.http);
    };

    clientSignup = () => {
        return new ClientSignup(this.http);
    };

    clientUser = () => {
        return new ClientUser(this.http);
    };

    computedLicence = () => {
        return new ComputedLicence(this.http);
    };

    coreUser = () => {
        return new CoreUser(this.http);
    };

    coupon = (): Coupon => {
        return new Coupon(this.http);
    };

    creditWallet = (): CreditWallet => {
        return new CreditWallet(this.http);
    };

    email = (): Email => {
        return new Email(this.http);
    };

    file = (): File => {
        return new File(this.http);
    };

    filter = (): Filter => {
        return new Filter(this.http);
    };

    illustrationLib = (): IllustrationLib => {
        return new IllustrationLib(this.http);
    };

    invoice = (): Invoice => {
        return new Invoice(this.http);
    };

    licence = (): Licence => {
        return new Licence(this.http);
    };

    me = (): Me => {
        return new Me(this.http);
    };

    notification = (): Notification => {
        return new Notification(this.http);
    };

    openTaskList = () => {
        return new OpenTaskList(this.http);
    };

    paymentAccount = (): PaymentAccount => {
        return new PaymentAccount(this.http);
    };

    paymentMethod = (): PaymentMethod => {
        return new PaymentMethod(this.http);
    };

    plan = (): Plan => {
        return new Plan(this.http);
    };

    price = (): Price => {
        return new Price(this.http);
    };

    product = (): Product => {
        return new Product(this.http);
    };

    quotaUsage = () => {
        return new QuotaUsage(this.http);
    };

    subscription = (): Subscription => {
        return new Subscription(this.http);
    };

    subscriptionPlan = (): SubscriptionPlan => {
        return new SubscriptionPlan(this.http);
    };

    task = (): Task => {
        return new Task(this.http);
    };

    taskCategory = (): TaskCategory => {
        return new TaskCategory(this.http);
    };

    taskCategoryAddon = (): TaskCategoryAddon => {
        return new TaskCategoryAddon(this.http);
    };

    taskCategoryFeaturedBlock = (): TaskCategoryFeaturedBlock => {
        return new TaskCategoryFeaturedBlock(this.http);
    };

    taskCategoryGroup = (): TaskCategoryGroup => {
        return new TaskCategoryGroup(this.http);
    };

    taskClientFeedback = (): TaskClientFeedback => {
        return new TaskClientFeedback(this.http);
    };

    taskComplexity = (): TaskComplexity => {
        return new TaskComplexity(this.http);
    };

    taskDeliverable = (): TaskDeliverable => {
        return new TaskDeliverable(this.http);
    };

    taskDeliverableComment = (): TaskDeliverableComment => {
        return new TaskDeliverableComment(this.http);
    };

    taskDeliverablePack = (): TaskDeliverablePack => {
        return new TaskDeliverablePack(this.http);
    };

    taskIssue = (): TaskIssue => {
        return new TaskIssue(this.http);
    };

    taskKanban = (): TaskKanban => {
        return new TaskKanban(this.http);
    };

    taskMessage = (): TaskMessage => {
        return new TaskMessage(this.http);
    };

    taskMessageTemplate = (): TaskMessageTemplate => {
        return new TaskMessageTemplate(this.http);
    };

    taskMessageTemplateCategory = (): TaskMessageTemplateCategory => {
        return new TaskMessageTemplateCategory(this.http);
    };

    taskPublish = (): TaskPublish => {
        return new TaskPublish(this.http);
    };

    taskSubTicket = (): TaskSubTicket => {
        return new TaskSubTicket(this.http);
    };

    taxId = (): TaxId => {
        return new TaxId(this.http);
    };

    team = (): Team => {
        return new Team(this.http);
    };

    teamOccupancy = (): TeamOccupancy => {
        return new TeamOccupancy(this.http);
    };

    timeTracking = (): TimeTracking => {
        return new TimeTracking(this.http);
    };

    UIUserTask = (): UIUserTask => {
        return new UIUserTask(this.http);
    };

    user = (): User => {
        return new User(this.http);
    };

    userEmailChange = (): UserEmailChange => {
        return new UserEmailChange(this.http);
    };

    userNote = (): UserNote => {
        return new UserNote(this.http);
    };

    userNotificationEmail = (): UserNotificationEmail => {
        return new UserNotificationEmail(this.http);
    };

    userOccupancy = (): UserOccupancy => {
        return new UserOccupancy(this.http);
    };

    userPasswordReset = (): UserPasswordReset => {
        return new UserPasswordReset(this.http);
    };

    userSpace = (): UserSpace => {
        return new UserSpace(this.http);
    };

    userVerification = (): UserVerification => {
        return new UserVerification(this.http);
    };

    userVerificationCode = (): UserVerificationCode => {
        return new UserVerificationCode(this.http);
    };

    virtualLicence = (): VirtualLicence => {
        return new VirtualLicence(this.http);
    };

    workspace = (): Workspace => {
        return new Workspace(this.http);
    };

    workspaceInitData = (): WorkspaceInitData => {
        return new WorkspaceInitData(this.http);
    };

    workspaceKey = (): WorkspaceKey => {
        return new WorkspaceKey(this.http);
    };

    workspaceSignup = (): WorkspaceSignup => {
        return new WorkspaceSignup(this.http);
    };

    workspaceUser = () => {
        return new WorkspaceUser(this.http);
    };
}

const repository = new Repository();
export default repository;
